import {
  addUserToGroup,
  disableUser,
  enableUser,
  listUsers,
  listUsersInGroup,
  rawUser,
  removeUserFromGroup,
} from "@/admin/services/adminQueries";
import { mapPool } from "@/shared/utils/async";
import { Profile, profiles } from "@/admin/services/profiles";
import { PROTECTED_GROUPS } from "@/admin/services/groups";

// Max number of group-membership queries in flight at once. The previous
// implementation fired one request per user with no cap, which throttled
// (HTTP 429) Cognito AdminQueries on large user pools.
const GROUP_QUERY_CONCURRENCY = 5;

export interface User {
  active: boolean;
  email: string;
  groups: string[];
  internalUsername: string;
}
class UserManager {
  async list(): Promise<User[]> {
    return this.listWithProfiles(await profiles.list());
  }

  /**
   * Build the user list, resolving each user's groups by querying membership
   * per group (a small, bounded set) instead of per user (unbounded). This
   * turns an O(users) request fan-out into O(groups), avoiding throttling.
   *
   * @param availableProfiles profiles already fetched by the caller, reused
   *   here to avoid a duplicate profiles.list() round-trip.
   */
  async listWithProfiles(availableProfiles: Profile[]): Promise<User[]> {
    const rawUsers = await listUsers();
    const allowedGroups = [
      ...PROTECTED_GROUPS,
      ...availableProfiles.map((p) => p.name),
    ];

    const groupsByUser = await this.buildGroupsByUser(allowedGroups);

    return rawUsers.map((rUser) => this.toUser(rUser, groupsByUser));
  }

  async disable(user: User) {
    await disableUser(user.internalUsername);
  }
  async enable(user: User) {
    await enableUser(user.internalUsername);
  }
  async removeFromGroup(user: User, group: string) {
    await removeUserFromGroup(user.internalUsername, group);
  }
  async addToGroup(user: User, group: string) {
    await addUserToGroup(user.internalUsername, group);
  }
  /**
   * Returns a map of username -> groups by querying members of each allowed
   * group concurrently (bounded) and inverting the result. A failed group
   * query is skipped rather than aborting the whole build.
   */
  private async buildGroupsByUser(
    allowedGroups: string[]
  ): Promise<Map<string, string[]>> {
    const settled = await mapPool(
      allowedGroups,
      async (group) => ({ group, members: await listUsersInGroup(group) }),
      GROUP_QUERY_CONCURRENCY
    );

    const groupsByUser = new Map<string, string[]>();
    for (const result of settled) {
      if (result.status !== "fulfilled") continue;
      const { group, members } = result.value;
      for (const member of members) {
        const existing = groupsByUser.get(member.Username);
        if (existing) {
          existing.push(group);
        } else {
          groupsByUser.set(member.Username, [group]);
        }
      }
    }
    return groupsByUser;
  }

  private toUser(
    rUser: rawUser,
    groupsByUser: Map<string, string[]>
  ): User {
    const email = rUser.Attributes.filter((e) => e.Name === "email").map(
      (e) => e.Value
    )[0];

    const groups = (groupsByUser.get(rUser.Username) ?? [])
      .slice()
      .sort();

    return {
      active: rUser.Enabled,
      email: email,
      groups: groups,
      internalUsername: rUser.Username,
    };
  }
}
export const users = new UserManager();
